import { createContext, type Dispatch, type FC, type PropsWithChildren, useContext, useMemo, useReducer } from "react";
import { editorActions } from "./actions";
import { editorReducer } from "./reducer";
import type { EditorAction, EditorState } from "./types";
import { initialState } from "./types";

interface EditorStateContextValue {
  state: EditorState;
}

interface EditorActionsContextValue {
  dispatch: Dispatch<EditorAction>;
  actions: typeof editorActions;
}

const EditorStateContext = createContext<EditorStateContextValue | null>(null);
const EditorActionsContext = createContext<EditorActionsContextValue | null>(null);

export const useEditorContext = () => {
  const stateContext = useContext(EditorStateContext);
  const actionsContext = useContext(EditorActionsContext);

  if (!stateContext || !actionsContext) {
    throw new Error("useEditorContext must be used within EditorProvider");
  }

  return {
    state: stateContext.state,
    dispatch: actionsContext.dispatch,
    actions: actionsContext.actions,
  };
};

/**
 * Internal provider that manages the editor state.
 * This component is separated so we can use React's key prop to force
 * complete remount (and state reset) when the session changes.
 *
 * Performance optimization: state and actions are in separate contexts
 * to prevent unnecessary re-renders of components that only use actions.
 */
const EditorProviderInternal: FC<PropsWithChildren & { initialEditorState?: EditorState }> = ({ children, initialEditorState }) => {
  const [state, dispatch] = useReducer(editorReducer, initialEditorState || initialState);

  // Memoize actions context (stable reference)
  const actionsValue = useMemo<EditorActionsContextValue>(
    () => ({
      dispatch,
      actions: editorActions,
    }),
    [], // Never changes - dispatch and editorActions are stable
  );

  // State context updates when state changes
  const stateValue = useMemo<EditorStateContextValue>(
    () => ({
      state,
    }),
    [state],
  );

  return (
    <EditorActionsContext.Provider value={actionsValue}>
      <EditorStateContext.Provider value={stateValue}>{children}</EditorStateContext.Provider>
    </EditorActionsContext.Provider>
  );
};

interface EditorProviderWrapperProps extends PropsWithChildren {
  initialEditorState?: EditorState;
  /**
   * sessionKey is used to force a complete state reset when it changes.
   * This ensures that each editing session starts with fresh state,
   * preventing issues like editor height persisting from previous edits.
   */
  sessionKey?: string;
}

/**
 * Wrapper component that uses the key prop to force remounting of
 * the internal provider when sessionKey changes.
 */
export const EditorProvider: FC<EditorProviderWrapperProps> = ({ children, initialEditorState, sessionKey }) => {
  // Using sessionKey as the key forces the entire provider subtree to remount
  // when it changes, ensuring completely fresh state for each edit session
  return (
    <EditorProviderInternal key={sessionKey} initialEditorState={initialEditorState}>
      {children}
    </EditorProviderInternal>
  );
};
